"use client";

import { useCallback, useRef, useSyncExternalStore } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { PatientJourneyFacts, PatientJourneyIdentity } from "@rmh/defs";
import { PATIENT_JOURNEY_QUERY_KEY } from "./keys";

export interface PatientJourneySessionReport {
  /** The pasted text the report was run from, and the key it is restored by. */
  input: string;
  identity: PatientJourneyIdentity;
  ranAt: number;
}

/**
 * Every report already held in this browser session's query cache, oldest first.
 * The cache is the session store: a report listed here restores from it without
 * reaching the network, so no source re-runs and no access record is emitted.
 */
const readReports = (client: QueryClient): PatientJourneySessionReport[] =>
  client
    .getQueryCache()
    .findAll({ queryKey: [PATIENT_JOURNEY_QUERY_KEY, "facts"] })
    .reduce<PatientJourneySessionReport[]>((reports, query) => {
      const data = query.state.data as PatientJourneyFacts | undefined;

      if (data) {
        reports.push({
          input: data.input,
          identity: data.identity,
          ranAt: query.state.dataUpdatedAt,
        });
      }

      return reports;
    }, [])
    .sort((a, b) => a.ranAt - b.ranAt);

const sameReports = (
  a: PatientJourneySessionReport[],
  b: PatientJourneySessionReport[]
): boolean =>
  a.length === b.length &&
  a.every(
    (report, index) =>
      report.input === b[index]?.input && report.ranAt === b[index]?.ranAt
  );

/**
 * Read as an external store rather than mirrored into state.
 *
 * The cache notifies while React is rendering — a hook that registers a query
 * during render makes it do so — and a `setState` from there is a render-phase
 * update, which re-renders unconditionally rather than bailing out on an equal
 * value. That is an infinite render, and it only appears once some other hook on
 * the page happens to touch the cache mid-render, which is why it must not be
 * written that way at all. For the same reason the subscription hands the change
 * over after the render that caused it, not from inside it.
 *
 * The snapshot is held so an unchanged list keeps its identity: `useSyncExternalStore`
 * compares the two by reference and would otherwise loop for the same reason.
 */
export const usePatientJourneySession = (): PatientJourneySessionReport[] => {
  const queryClient = useQueryClient();
  const snapshot = useRef<PatientJourneySessionReport[]>([]);

  const subscribe = useCallback(
    (onChange: () => void) => {
      let live = true;

      // The cache notifies synchronously while React is rendering — registering a
      // query during a render adds it to the cache — so the change is handed over
      // once that render has committed rather than from inside it.
      const unsubscribe = queryClient.getQueryCache().subscribe(() => {
        queueMicrotask(() => {
          if (live) onChange();
        });
      });

      return () => {
        live = false;
        unsubscribe();
      };
    },
    [queryClient]
  );

  const getSnapshot = useCallback(() => {
    const next = readReports(queryClient);

    if (!sameReports(snapshot.current, next)) {
      snapshot.current = next;
    }

    return snapshot.current;
  }, [queryClient]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};
